/**
 * BriefingPanel — Phase 032 Plan 02 (D-06 REQ-BASE-04) 5th DS sub-tab.
 *
 * Operator-authored briefings persist as a `briefing` block-kind in
 * `application_ontology` via the SHIPPED Phase 031 substrate. The
 * canonical envelope shape (`kind:'briefing'` + `properties.{title,body,
 * author,created_at}`) was locked by
 * `.planning/phases/032-tier-2-real-app-acceptance/032-02-PREVIEW-PROBE.md`
 * after a 2026-05-18 non-mutating probe of the live SHACL gate returned
 * `ok:true, inserted_count:3, audit_source:webmcp`.
 *
 * Read+write contracts:
 *   - WRITE: `useBriefings().addBriefing(draft, authorId)` →
 *     `useMutationStore.addBlock` (Phase 031 Plan 03 substrate) → live
 *     `cogo.ontology.add_block` via the wired 4-tool client.
 *   - READ: PostgREST direct query against `application_ontology`
 *     (the removed JsonbAboxSource graph view-model surfaced only
 *     cobo:Archetype instances; briefings are a different kind).
 *
 * Security (PATTERNS §4.5 / RESEARCH §"Security Domain"):
 *   - The body field is operator-authored markdown (XSS vector).
 *   - The panel renders the body via a plain `<pre>{body}</pre>` text
 *     node — NEVER via React's raw-HTML escape hatch. The `<pre>`
 *     element keeps newlines visible but does not interpret HTML;
 *     React escapes any embedded `<script>` / `<img onerror>` / etc.
 *
 * Tenant guard (RESEARCH §"Security Domain" T-032-02-03 + Phase 031 D-02):
 *   - When `deriveTenantId(session) === null`, render an explicit
 *     `briefing-no-tenant` testid — never pass `tenantId=""` (that would
 *     reintroduce the removed GAP-06 dev-guard).
 */

import { useCallback, useState } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { deriveTenantId } from '@/store/auth-slice'
import { BriefingLoadError } from './briefing-load-error'
import { useBriefings } from './use-briefing'
import type { BriefingDraft } from './types'
import { useStudioLocale } from '@/i18n/use-studio-locale'
import { getStudioAuthProvider } from '@/services/cogo-platform-config'

const APP_ID = 'demo' as const

export function BriefingPanel() {
  const { strings } = useStudioLocale()
  const copy = strings.designSystem.validatorPanel
  const session = useStudioStore((s) => s.session)
  const tenantId = deriveTenantId(session)

  // PATTERNS §4.5 + RESEARCH §"Security Domain": no-tenant guard renders
  // an explicit testid BEFORE invoking the hook so we never read with a
  // missing-tenant opts shape (Pitfall 3 closure).
  if (tenantId === null) {
    return (
      <div
        data-testid="briefing-no-tenant"
        className="flex h-full min-h-0 items-center justify-center p-5"
      >
        <div className="max-w-md rounded-md border border-border bg-background p-6 text-center">
          <h2 className="text-base font-semibold">{copy.briefingSignIn}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {copy.briefingSignedOutDetail}
          </p>
        </div>
      </div>
    )
  }

  // DGX's authenticated platform API currently has no briefing read/write
  // contract. The historical implementation below reads PostgREST directly
  // and writes through the Supabase-backed ontology gateway, so mounting it
  // under Cogo auth would both fail the read and present a write control with
  // no supported destination. Keep this fail-closed until the owning DGX API
  // ships an explicit briefing contract.
  if (getStudioAuthProvider() === 'cogo') {
    return (
      <div
        data-testid="briefing-unavailable"
        className="flex h-full min-h-0 items-center justify-center p-5"
      >
        <div className="max-w-md rounded-md border border-border bg-background p-6 text-center">
          <h2 className="text-base font-semibold">{copy.briefingUnavailableTitle}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {copy.briefingUnavailableDetail}
          </p>
        </div>
      </div>
    )
  }

  return <BriefingPanelInner tenantId={tenantId} appId={APP_ID} />
}

interface BriefingPanelInnerProps {
  tenantId: string
  appId: string
}

function BriefingPanelInner({ tenantId, appId }: BriefingPanelInnerProps) {
  const { strings } = useStudioLocale()
  const copy = strings.designSystem.validatorPanel
  const session = useStudioStore((s) => s.session)
  const authorId = session?.user?.id ?? tenantId
  const { items, loading, error, addBriefing } = useBriefings(tenantId, appId)

  const [draft, setDraft] = useState<BriefingDraft>({ title: '', body: '' })
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<Error | null>(null)

  const onSave = useCallback(async () => {
    if (draft.title.trim() === '' || draft.body.trim() === '') return
    setSaving(true)
    setSaveError(null)
    try {
      await addBriefing(draft, authorId)
      setDraft({ title: '', body: '' })
    } catch (e) {
      setSaveError(e instanceof Error ? e : new Error(String(e)))
    } finally {
      setSaving(false)
    }
  }, [draft, authorId, addBriefing])

  const canSave =
    draft.title.trim() !== '' && draft.body.trim() !== '' && !saving

  return (
    <div
      data-testid="briefing-panel"
      className="flex h-full min-h-0 flex-col gap-4 p-5"
    >
      <header className="flex items-baseline justify-between">
        <div>
          <h2 className="text-base font-semibold">{copy.briefingTitle}</h2>
          <p className="text-xs text-muted-foreground">
            {copy.briefingIntro}
          </p>
        </div>
        <span
          data-testid="briefing-count"
          className="text-xs text-muted-foreground"
        >
          {items.length} {items.length === 1 ? 'briefing' : 'briefings'}
        </span>
      </header>

      <section
        data-testid="briefing-composer"
        className="flex flex-col gap-2 rounded-md border border-border bg-background p-4"
      >
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">
            {copy.briefingFieldTitle}
          </span>
          <input
            data-testid="briefing-composer-title"
            type="text"
            value={draft.title}
            onChange={(e) =>
              setDraft((d) => ({ ...d, title: e.target.value }))
            }
            placeholder={copy.briefingTitlePlaceholder}
            className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
            disabled={saving}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">
            {copy.briefingFieldBody}
          </span>
          <textarea
            data-testid="briefing-composer-body"
            value={draft.body}
            onChange={(e) =>
              setDraft((d) => ({ ...d, body: e.target.value }))
            }
            placeholder={copy.briefingBodyPlaceholder}
            rows={5}
            className="resize-vertical rounded-md border border-input bg-background px-3 py-1.5 font-mono text-xs"
            disabled={saving}
          />
        </label>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {saveError !== null
              ? `Save failed: ${saveError.message}`
              : saving
                ? copy.briefingSaving
                : copy.briefingReady}
          </span>
          <button
            data-testid="briefing-composer-save"
            type="button"
            onClick={() => {
              void onSave()
            }}
            disabled={!canSave}
            className="rounded-md bg-secondary px-3 py-1.5 text-sm font-medium text-secondary-foreground disabled:opacity-50"
          >
            {copy.briefingSave}
          </button>
        </div>
      </section>

      <section
        data-testid="briefing-list"
        className="flex-1 min-h-0 overflow-y-auto rounded-md border border-border bg-background"
      >
        {loading && items.length === 0 ? (
          <div
            data-testid="briefing-list-loading"
            className="p-4 text-sm text-muted-foreground"
          >
            Loading briefings…
          </div>
        ) : error !== null ? (
          <BriefingLoadError error={error} />
        ) : items.length === 0 ? (
          <div
            data-testid="briefing-list-empty"
            className="p-4 text-sm text-muted-foreground"
          >
            {copy.briefingEmpty}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((item) => (
              <li
                key={item.id}
                data-testid="briefing-item"
                className="flex flex-col gap-1 p-4"
              >
                <div className="flex items-baseline justify-between">
                  <h3 className="text-sm font-semibold">{item.title}</h3>
                  <span className="text-xs text-muted-foreground">
                    {item.created_at}
                  </span>
                </div>
                {/*
                  PATTERNS §4.5 anti-pattern guard: render body as escaped
                  text only — `<pre>` preserves newlines from the operator's
                  markdown body while React escapes any HTML/script content
                  embedded in `item.body`. This is the XSS-safe rendering
                  path; never use React's raw-HTML escape hatch here.
                */}
                <pre className="whitespace-pre-wrap break-words font-mono text-xs text-muted-foreground">
                  {item.body}
                </pre>
                <span className="text-[10px] text-muted-foreground">
                  by {item.author}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
